/**
 * normalizeOnPredicate — display-only normalizer for the MERGE ON-card
 * predicate (EDG-27). Some engines' MERGE grammar wraps the ON predicate in
 * a redundant outer paren pair (Oracle's `MERGE … ON (…)`), others don't
 * (mssql's bare `ON …`). Both express the identical predicate — strip the
 * outer pair ONLY when it truly encloses the whole expression, so the same
 * shape renders identically across engines.
 *
 * Token/paren-aware: reuses `consumeParenGroup` (render/insert/insertParseHelpers)
 * plus `skipContextFor`/`trySkipNonCode` (sql-resolver/classify/tokenSkip) so a
 * `)` inside a string literal or a nested `(…)` group is never mistaken for
 * the outer close. Purely a rendered-label transform — the buffer / emitted
 * SQL and the outline's `onStart`/`onEnd` span are never touched.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor } from '../../../../sql-resolver/classify/tokenSkip';
import { consumeParenGroup } from '../insert/insertParseHelpers';

export function normalizeOnPredicate(onText: string, options: ParseOptions): string {
  const text = onText.trim();
  if (text.length === 0 || text[0] !== '(') return text;

  const ctx = skipContextFor(options);
  const closeIdx = consumeParenGroup(text, 0, text.length, ctx);

  // The `(` at position 0 is redundant only if its matching `)` lands
  // exactly at the end of the predicate — i.e. that pair encloses the WHOLE
  // expression (not just a leading clause, e.g. `(a = b) AND (c = d)`, where
  // the first `(` closes mid-string). Also require the last character to
  // actually be `)` so an unbalanced predicate (parens never close) can't
  // be misread as "reached the end" and stripped.
  if (closeIdx !== text.length || text[text.length - 1] !== ')') return text;

  // Strip exactly one redundant pair — doubly-wrapped predicates like
  // `((a = b))` keep their inner pair (`(a = b)`); this stays deterministic
  // and matches how the ON card already renders any other parenthesized
  // sub-expression verbatim.
  return text.slice(1, -1).trim();
}
